import {
  Broadcast,
  Check,
  CheckCircle,
  CircleNotch,
  Link as LinkIcon,
  Plus,
  Trash,
  Warning,
} from "@phosphor-icons/react";
import { type ReactNode, useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import {
  type AdminHouseholdDetail,
  type AirthingsPreconfigEntry,
  type GatewayInfo,
  getApiErrorMessage,
  type PreconfigUpdate,
  useRevealHouseholdSecrets,
  useUpdatePreconfig,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { looksLike5GhzSsid } from "@/lib/wifi-band";

import { MissingChip } from "./missing-chip";
import { WifiLinkDialog } from "./wifi-link-dialog";
import { WifiPasswordDeleteDialog } from "./wifi-password-delete-dialog";

interface PreconfigCardProps {
  household: AdminHouseholdDetail;
}

const FIELD_LABEL = "text-muted type-label";
const SECTION_LABEL = "text-primary type-label";

/**
 * Labeled text input that saves on blur/Enter, mirroring the click-to-edit
 * affordance of `InlineEdit` but staying visible as a form field (needed
 * here so validation errors — e.g. a rejected MAC — have somewhere to
 * render). Draft resyncs from `value` whenever the saved value changes.
 */
function TextField({
  label,
  value,
  onSave,
  placeholder,
  monospace,
  missing,
  warning,
}: {
  label: string;
  value: string;
  onSave: (value: string) => Promise<void>;
  placeholder?: string;
  monospace?: boolean;
  missing?: boolean;
  /** Something about the value itself that staff should see before the kit ships. */
  warning?: string;
}) {
  const [draft, setDraft] = useState(value);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(value);
    setStatus("idle");
    setError("");
  }, [value]);

  const commit = async () => {
    if (draft === value) {
      return;
    }
    setStatus("saving");
    setError("");
    try {
      await onSave(draft);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch (err) {
      setStatus("error");
      setError(getApiErrorMessage(err, "Failed to save."));
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className={FIELD_LABEL}>{label}</label>
        {missing && <MissingChip variant="preship" />}
      </div>
      <div className="flex items-center gap-1.5">
        <Input
          aria-label={label}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          className={cn(
            "h-9 text-[13.5px]",
            monospace && "font-mono",
            status === "error" && "border-destructive",
          )}
        />
        {status === "saving" && (
          <CircleNotch className="text-muted size-3.5 shrink-0 animate-spin" weight="light" />
        )}
        {status === "saved" && <Check className="text-success size-3.5 shrink-0" weight="light" />}
      </div>
      {warning && <p className="text-warning text-[11.5px]">{warning}</p>}
      {status === "error" && error && <p className="text-destructive text-[12px]">{error}</p>}
    </div>
  );
}

/** Write-only secret field: shows a masked "set" state, never a real value. */
function PasswordField({
  label,
  hasValue,
  onSave,
  hint,
  missing,
  revealFetcher,
}: {
  label: string;
  hasValue: boolean;
  onSave: (value: string) => Promise<void>;
  hint?: string;
  missing?: boolean;
  revealFetcher?: () => Promise<string | null | undefined>;
}) {
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState("");

  const commit = async () => {
    if (!draft) {
      return;
    }
    setStatus("saving");
    setError("");
    try {
      await onSave(draft);
      setDraft("");
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch (err) {
      setStatus("error");
      setError(getApiErrorMessage(err, "Failed to save."));
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className={FIELD_LABEL}>{label}</label>
        {missing ? (
          <MissingChip variant="preship" />
        ) : (
          hasValue && (
            <span className="bg-accent/10 text-accent-pressed rounded-sm px-2 py-0.5 text-[11px] font-semibold">
              set ✓
            </span>
          )
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <PasswordInput
          aria-label={label}
          value={draft}
          revealFetcher={revealFetcher}
          placeholder={hasValue ? "•••••• (leave blank to keep)" : "Not set"}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          className={cn("h-9 text-[13.5px]", status === "error" && "border-destructive")}
        />
        {status === "saving" && (
          <CircleNotch className="text-muted size-3.5 shrink-0 animate-spin" weight="light" />
        )}
        {status === "saved" && <Check className="text-success size-3.5 shrink-0" weight="light" />}
      </div>
      {hint && <p className="text-muted text-[11.5px]">{hint}</p>}
      {status === "error" && error && <p className="text-destructive text-[12px]">{error}</p>}
    </div>
  );
}

function Section({
  title,
  action,
  children,
  missing,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  missing?: boolean;
}) {
  return (
    <div className="border-border-subtle border-t pt-4 first:border-t-0 first:pt-0">
      <div className="mb-2.5 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <span className={SECTION_LABEL}>{title}</span>
          {missing && <MissingChip variant="preship" />}
        </span>
        {action}
      </div>
      {children}
    </div>
  );
}

function GatewaySection({
  title,
  gateway,
  onSave,
  macMissing,
}: {
  title: string;
  gateway: GatewayInfo;
  onSave: (patch: Partial<GatewayInfo>) => Promise<void>;
  macMissing?: boolean;
}) {
  return (
    <div className="bg-background rounded-xl p-3.5">
      <div className="text-primary mb-2.5 text-[13.5px] font-bold">{title}</div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TextField
          label="MAC address"
          value={gateway.mac ?? ""}
          placeholder="AA:BB:CC:DD:EE:FF"
          monospace
          missing={macMissing}
          onSave={(v) => onSave({ mac: v || null })}
        />
        <div className="flex flex-col gap-1.5">
          <TextField
            label="IP / URL"
            value={gateway.url ?? ""}
            placeholder="192.168.1.x"
            onSave={(v) => onSave({ url: v || null })}
          />
          <p className="text-muted text-[11.5px]">
            Filled at installation (DHCP) — may be empty before shipping.
          </p>
        </div>
        <TextField
          label="Notes"
          value={gateway.notes ?? ""}
          placeholder="Optional"
          onSave={(v) => onSave({ notes: v || null })}
        />
      </div>
    </div>
  );
}

/**
 * Labeled room picker for an AirThings sensor — an enumerated selector over
 * the household's rooms (no free text: the flow places sensors by room, so
 * a typo'd location would silently orphan the sensor).
 */
function RoomSelect({
  label,
  value,
  roomNames,
  onSave,
}: {
  label: string;
  value: string;
  roomNames: string[];
  onSave: (value: string) => Promise<void>;
}) {
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState("");
  // A previously saved free-text location that matches no room still needs
  // to be visible (and replaceable) rather than silently blanked.
  const options = value && !roomNames.includes(value) ? [value, ...roomNames] : roomNames;

  const commit = async (next: string) => {
    if (next === value) {
      return;
    }
    setStatus("saving");
    setError("");
    try {
      await onSave(next);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch (err) {
      setStatus("error");
      setError(getApiErrorMessage(err, "Failed to save."));
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label className={FIELD_LABEL}>{label}</label>
      <div className="flex items-center gap-1.5">
        <select
          aria-label={label}
          value={value}
          onChange={(e) => void commit(e.target.value)}
          className={cn(
            "border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none",
            "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            !value && "text-muted",
          )}
        >
          <option value="">Unassigned</option>
          {options.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        {status === "saving" && (
          <CircleNotch className="text-muted size-4 shrink-0 animate-spin" weight="light" />
        )}
        {status === "saved" && <Check className="text-success size-4 shrink-0" weight="light" />}
      </div>
      {status === "error" && <p className="text-destructive text-[12px]">{error}</p>}
    </div>
  );
}

/**
 * Pre-ship configuration card: the network/gateway/credential info an admin
 * fills in before a unit ships, plus a readiness banner mirroring the
 * command band's handoff meter. Rendered first in the main column since
 * pre-ship readiness is the most time-sensitive concern on this page.
 */
export function PreconfigCard({ household }: PreconfigCardProps) {
  const { preconfig } = household;
  const roomNames = household.rooms.map((room) => room.name);
  const updateMutation = useUpdatePreconfig();
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [deletePasswordOpen, setDeletePasswordOpen] = useState(false);

  const savePatch = (patch: PreconfigUpdate) =>
    updateMutation.mutateAsync({ id: household.id, body: patch }).then(() => undefined);

  const saveGateway = (key: "gateway_wavin" | "gateway_nilan", patch: Partial<GatewayInfo>) => {
    const current = preconfig[key] ?? {};
    const nextGateway: GatewayInfo = { ...current, ...patch };
    return key === "gateway_wavin"
      ? savePatch({ gateway_wavin: nextGateway })
      : savePatch({ gateway_nilan: nextGateway });
  };

  const airthings = preconfig.airthings ?? [];
  const saveAirthings = (next: AirthingsPreconfigEntry[]) => savePatch({ airthings: next });

  const revealMutation = useRevealHouseholdSecrets();
  const revealSecret = (key: "wifi_password" | "velux_klg_password" | "ha_login_password") => () =>
    revealMutation.mutateAsync({ id: household.id }).then((secrets) => secrets?.[key]);

  const missingCount = household.preconfig_missing.length;
  const isMissing = (item: string) => household.preconfig_missing.includes(item);

  return (
    <div className="bg-card shadow-elevation rounded-md">
      <div className="border-border-subtle flex items-center gap-2.5 border-b px-5 py-[18px]">
        <Broadcast className="text-muted size-[18px]" weight="light" />
        <h2 className="text-primary text-[17px] font-bold">Pre-configuration</h2>
      </div>

      <div className="px-5 pt-4 pb-1">
        {household.preconfig_ready ? (
          <div className="bg-success/10 mb-4 flex items-center gap-2.5 rounded-xl p-3">
            <CheckCircle className="text-success size-[18px] shrink-0" weight="light" />
            <span className="text-success text-[14px] font-semibold">Ready to ship</span>
          </div>
        ) : (
          <div className="bg-warning/10 mb-4 rounded-xl p-3">
            <div className="flex items-center gap-2.5">
              <Warning className="text-warning size-[18px] shrink-0" weight="light" />
              <span className="text-warning text-[14px] font-semibold">
                {missingCount} item{missingCount === 1 ? "" : "s"} to configure before shipping
              </span>
            </div>
            {missingCount > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {household.preconfig_missing.map((item) => (
                  <span
                    key={item}
                    className="border-warning/25 text-warning inline-flex items-center rounded-sm border bg-transparent px-2.5 py-1 text-[11.5px] font-medium"
                  >
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 px-5 pb-5">
        <Section title="WiFi">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            {preconfig.wifi_source === "household" ? (
              <span className="text-muted text-[12px]">
                Submitted by the household
                {preconfig.wifi_submitted_at
                  ? ` on ${new Date(preconfig.wifi_submitted_at).toLocaleDateString()}`
                  : ""}
              </span>
            ) : (
              <span className="text-muted text-[12px]">Entered by staff</span>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setLinkDialogOpen(true)}
                className="text-primary focus-visible:outline-ring flex items-center gap-1 text-[12px] hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <LinkIcon className="size-3.5" weight="light" />
                Sharing link
              </button>
              {preconfig.has_wifi_password && (
                <button
                  type="button"
                  onClick={() => setDeletePasswordOpen(true)}
                  className="text-destructive focus-visible:outline-ring flex items-center gap-1 text-[12px] hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  <Trash className="size-3.5" weight="light" />
                  Delete now
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="SSID"
              value={preconfig.wifi_ssid ?? ""}
              placeholder="Network name"
              missing={isMissing("WiFi network name")}
              warning={
                looksLike5GhzSsid(preconfig.wifi_ssid)
                  ? "This reads like a 5 GHz network. The connectors only join 2.4 GHz, so enter that name here."
                  : undefined
              }
              onSave={(v) => savePatch({ wifi_ssid: v || null })}
            />
            <PasswordField
              label="Password"
              hasValue={!!preconfig.has_wifi_password}
              missing={isMissing("WiFi password")}
              revealFetcher={revealSecret("wifi_password")}
              onSave={(v) => savePatch({ wifi_password: v })}
            />
          </div>
        </Section>

        <Section title="Gateways">
          <div className="flex flex-col gap-3">
            <GatewaySection
              title="Wavin gateway"
              gateway={preconfig.gateway_wavin ?? {}}
              macMissing={isMissing("Wavin gateway MAC address")}
              onSave={(patch) => saveGateway("gateway_wavin", patch)}
            />
            <GatewaySection
              title="Nilan gateway"
              gateway={preconfig.gateway_nilan ?? {}}
              macMissing={isMissing("Nilan gateway MAC address")}
              onSave={(patch) => saveGateway("gateway_nilan", patch)}
            />
          </div>
        </Section>

        <Section title="VELUX KLG300">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <TextField
              label="MAC address"
              value={preconfig.velux_klg_mac ?? ""}
              placeholder="AA:BB:CC:DD:EE:FF"
              monospace
              missing={isMissing("VELUX KLG300 MAC address")}
              onSave={(v) => savePatch({ velux_klg_mac: v || null })}
            />
            <PasswordField
              label="Gateway password"
              hasValue={!!preconfig.has_velux_klg_password}
              missing={isMissing("VELUX gateway password")}
              revealFetcher={revealSecret("velux_klg_password")}
              onSave={(v) => savePatch({ velux_klg_password: v })}
            />
          </div>
        </Section>

        <Section title="Home Assistant login">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TextField
              label="Username"
              value={preconfig.ha_login_username ?? ""}
              missing={isMissing("Home Assistant login username")}
              onSave={(v) => savePatch({ ha_login_username: v || null })}
            />
            <PasswordField
              label="Password"
              hasValue={!!preconfig.has_ha_login_password}
              missing={isMissing("Home Assistant login password")}
              revealFetcher={revealSecret("ha_login_password")}
              onSave={(v) => savePatch({ ha_login_password: v })}
            />
          </div>
          <p className="text-muted mt-2 text-[11.5px]">URL &amp; token are under Home Assistant.</p>
        </Section>

        <Section
          title={`AirThings sensors · ${airthings.length}`}
          missing={isMissing("At least one AirThings sensor serial")}
          action={
            <button
              type="button"
              onClick={() => void saveAirthings([...airthings, { sensor_id: "", location: "" }])}
              className="text-primary focus-visible:outline-ring inline-flex items-center gap-1 text-[13px] font-semibold hover:underline focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <Plus className="size-3.5" weight="light" />
              Add sensor
            </button>
          }
        >
          <div className="mb-3 flex flex-col gap-1.5">
            <TextField
              label="AirThings building ID"
              value={preconfig.airthings_location_id ?? ""}
              monospace
              placeholder="From the AirThings dashboard"
              missing={isMissing("AirThings building ID")}
              onSave={(v) => savePatch({ airthings_location_id: v || null })}
            />
            <p className="text-muted text-[11.5px]">
              One building per house in the AirThings dashboard — its sensors live inside it. The
              flow&apos;s cloud checks read this building.
            </p>
          </div>
          {airthings.length === 0 ? (
            <p className="text-muted text-[13.5px]">No sensors pre-provisioned yet.</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {airthings.map((entry, i) => (
                <div key={i} className="bg-background flex items-start gap-2.5 rounded-xl p-3">
                  <div className="grid grow grid-cols-1 gap-3 sm:grid-cols-2">
                    <TextField
                      label="Sensor Serial"
                      value={entry.sensor_id}
                      monospace
                      onSave={(v) =>
                        saveAirthings(
                          airthings.map((e, idx) => (idx === i ? { ...e, sensor_id: v } : e)),
                        )
                      }
                    />
                    <RoomSelect
                      label="Room"
                      value={entry.location ?? ""}
                      roomNames={roomNames}
                      onSave={(v) =>
                        saveAirthings(
                          airthings.map((e, idx) =>
                            idx === i ? { ...e, location: v || null } : e,
                          ),
                        )
                      }
                    />
                  </div>
                  <button
                    type="button"
                    aria-label="Remove sensor"
                    onClick={() => void saveAirthings(airthings.filter((_, idx) => idx !== i))}
                    className="text-muted hover:text-destructive mt-6 shrink-0"
                  >
                    <Trash className="size-4" weight="light" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {airthings.length > 0 && (
            <p className="text-muted mt-2 text-[11.5px]">
              Each sensor is mirrored into its room&apos;s devices on save — the onboarding flow
              places and verifies exactly the sensors that have a room. Leave a sensor{" "}
              <span className="font-semibold">Unassigned</span> to park it as inventory (e.g. not
              yet powered on): the household&apos;s checks won&apos;t wait for it.
            </p>
          )}
        </Section>
      </div>

      <WifiLinkDialog
        householdId={household.id}
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
      />
      <WifiPasswordDeleteDialog
        householdId={household.id}
        networkName={preconfig.wifi_ssid}
        open={deletePasswordOpen}
        onOpenChange={setDeletePasswordOpen}
      />
    </div>
  );
}
